<template>
  <section v-if="!capabilities" class="c2c-cap-loading" :aria-label="t('capability.checking')">
    <div class="c2c-cap-spinner" aria-hidden="true"></div>
    <p>{{ t('capability.checking') }}</p>
  </section>

  <section v-else-if="!capabilities.canEncode && !capabilities.canPassthrough" class="c2c-cap-blocked">
    <div class="c2c-cap-head">
      <AlertTriangle :size="20" />
      <h2>{{ t('capability.blocked_heading') }}</h2>
    </div>
    <p>{{ capabilities.reason || t('capability.blocked_generic') }}</p>
    <p class="c2c-cap-fallback">
      <i18n-t keypath="capability.blocked_fallback" tag="span">
        <template #v2c>
          <a href="https://commons.wikimedia.org/wiki/Commons:Video2commons" target="_blank" rel="noopener noreferrer">video2commons</a>
        </template>
        <template #wizard>
          <a href="https://commons.wikimedia.org/wiki/Special:UploadWizard" target="_blank" rel="noopener noreferrer">UploadWizard</a>
        </template>
      </i18n-t>
    </p>
  </section>

  <section v-else-if="!capabilities.canEncode" class="c2c-cap-warn">
    <div class="c2c-cap-head">
      <AlertTriangle :size="20" />
      <strong>{{ t('capability.passthrough_only_heading') }}</strong>
    </div>
    <p>{{ capabilities.reason }}</p>
    <p class="c2c-cap-detail">{{ t('capability.passthrough_only_detail') }}</p>
  </section>

  <section
    v-if="capabilities && capabilities.details.memoryLimited"
    class="c2c-cap-info"
  >
    <div class="c2c-cap-head">
      <Info :size="20" />
      <strong>{{ t('capability.memory_limited_heading') }}</strong>
    </div>
    <p v-if="capabilities.details.deviceMemoryGiB !== undefined">{{ t('capability.memory_limited_detail', {
      mb: Math.round(capabilities.effectiveMaxBytes / 1024 / 1024),
      ram: capabilities.details.deviceMemoryGiB
}) }}</p>
    <p v-else>{{ t('capability.memory_limited_unknown_detail', {
      mb: Math.round(capabilities.effectiveMaxBytes / 1024 / 1024)
}) }}</p>
  </section>

  <section
    v-if="showThrottleWarning"
    class="c2c-cap-warn"
    role="alert"
  >
    <div class="c2c-cap-head">
      <AlertTriangle :size="20" />
      <strong>{{ t('capability.visibility_throttle_heading') }}</strong>
    </div>
    <p>{{ t('capability.visibility_throttle_detail') }}</p>
  </section>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed } from "vue"
import { AlertTriangle, Info } from "lucide-vue-next"
import { useI18n } from "vue-i18n"
import type { Capabilities } from "@/lib/capability"

const { t } = useI18n()

const props = defineProps<{
  capabilities: Capabilities | null
  /** When true, this component shows a 'browser is throttling background
   *  work, foreground the tab to keep it moving' alert if the page goes
   *  hidden. Set by the parent only when work is in flight. */
  watchVisibility?: boolean
}>()

const isHidden = ref(typeof document !== "undefined" && document.hidden)

function onVis() { isHidden.value = document.hidden }

onMounted(() => {
  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", onVis)
  }
})
onUnmounted(() => {
  if (typeof document !== "undefined") {
    document.removeEventListener("visibilitychange", onVis)
  }
})

const showThrottleWarning = computed(() =>
  !!props.watchVisibility && isHidden.value
)
</script>

<style scoped>
.c2c-cap-loading {
  display: flex; align-items: center; gap: 12px;
  padding: 12px 16px; margin-bottom: 16px;
  color: #54595d; font-size: 13px;
}
.c2c-cap-spinner {
  width: 16px; height: 16px;
  border: 2px solid #c8ccd1;
  border-top-color: #3366cc;
  border-radius: 50%;
  animation: c2c-cap-spin 0.8s linear infinite;
}
@keyframes c2c-cap-spin { to { transform: rotate(360deg); } }
.c2c-cap-loading p { margin: 0; }

.c2c-cap-blocked {
  background: #fee7e6; border: 1px solid #d73333; border-radius: 6px;
  padding: 16px; margin-bottom: 16px;
}
.c2c-cap-blocked .c2c-cap-head { color: #b32424; }
.c2c-cap-blocked h2 { margin: 0; font-size: 16px; }

.c2c-cap-warn {
  background: #fef6e7; border: 1px solid #f0d795; border-radius: 6px;
  padding: 12px 14px; margin-bottom: 16px;
}
.c2c-cap-warn .c2c-cap-head { color: #886600; }
.c2c-cap-warn strong { font-size: 14px; }

.c2c-cap-info {
  background: #eaf3ff; border: 1px solid #a3c8f3; border-radius: 6px;
  padding: 12px 14px; margin-bottom: 16px;
}
.c2c-cap-info .c2c-cap-head { color: #2a4b8d; }
.c2c-cap-info strong { font-size: 14px; }

.c2c-cap-head {
  display: flex; align-items: center; gap: 8px;
  margin-bottom: 6px;
}
.c2c-cap-head h2 { font-size: 16px; }

.c2c-cap-blocked p, .c2c-cap-warn p, .c2c-cap-info p {
  margin: 4px 0; font-size: 13px;
}
.c2c-cap-fallback { color: #54595d; }
.c2c-cap-fallback a { color: #3366cc; }
.c2c-cap-detail { color: #54595d; }
</style>

